import {
  DEFAULT_PATTERN,
  GRADIENT_PRESETS,
  type Look,
  type LookStyle,
  STANDARD_LOOK,
} from '@/entities/appearance';

const FALLBACK_COLOR = '#0079bf';

/** Styles painted with one color (or three, in advanced mode). */
export const COLORED_STYLES: readonly LookStyle[] = ['header', 'border', 'accent', 'soft', 'solid'];

export function isColoredStyle(style: LookStyle): boolean {
  return COLORED_STYLES.includes(style);
}

/** Switch the style, keeping what still applies (color, gradient, pattern). */
export function withStyle(look: Look | null, style: LookStyle): Look | null {
  if (style === 'standard') {
    // No fill, but explicit text colors / the content surface are kept.
    const kept = withTextOnly(look);
    return kept;
  }
  const current = look ?? STANDARD_LOOK;
  const color =
    current.color ?? current.gradient?.start ?? current.pattern?.color ?? FALLBACK_COLOR;
  if (style === 'gradient') {
    const preset = GRADIENT_PRESETS[0];
    return {
      ...current,
      style,
      one_color: true,
      color: null,
      advanced: null,
      gradient: current.gradient ?? (preset ? { ...preset } : null),
      pattern: null,
    };
  }
  if (style === 'pattern') {
    return {
      ...current,
      style,
      one_color: true,
      color: null,
      advanced: null,
      gradient: null,
      pattern: current.pattern ?? { ...DEFAULT_PATTERN, color },
    };
  }
  return { ...current, style, color, gradient: null, pattern: null };
}

/** "One color" off exposes header / border / body colors, seeded from the single color. */
export function withOneColor(look: Look, oneColor: boolean): Look {
  if (oneColor) return { ...look, one_color: true, advanced: null };
  const seed = look.color ?? FALLBACK_COLOR;
  return {
    ...look,
    one_color: false,
    advanced: look.advanced ?? { header: seed, border: seed, body: null },
  };
}

const TEXT_KEYS = ['title_color', 'text_color', 'muted_color', 'surface'] as const;

export function hasTextColors(look: Look | null): boolean {
  return look !== null && TEXT_KEYS.some((key) => look[key]);
}

/** A standard (unfilled) look that keeps only the explicit text colors, or `null` when none. */
function withTextOnly(look: Look | null): Look | null {
  if (!hasTextColors(look)) return null;
  return {
    ...STANDARD_LOOK,
    title_color: look?.title_color ?? null,
    text_color: look?.text_color ?? null,
    muted_color: look?.muted_color ?? null,
    surface: look?.surface ?? null,
  };
}

/** Set (or clear) one text color; a standard look without any text color collapses to `null`. */
export function withTextColor(
  look: Look | null,
  key: (typeof TEXT_KEYS)[number],
  value: string | null,
): Look | null {
  const next: Look = { ...(look ?? STANDARD_LOOK), [key]: value };
  return next.style === 'standard' && !hasTextColors(next) ? null : next;
}

/** A look the server will accept (and that actually paints something). */
export function isLookValid(look: Look | null): boolean {
  if (look === null) return true;
  if (look.style === 'standard') return true;
  if (look.style === 'gradient') return look.gradient !== null;
  if (look.style === 'pattern') return look.pattern !== null;
  if (look.one_color) return look.color !== null;
  const advanced = look.advanced;
  return advanced !== null && (advanced.header ?? advanced.border ?? advanced.body) !== null;
}

/** What is sent to the API: the look, or `null` for "standard". */
export function lookPayload(look: Look | null): Look | null {
  if (look === null) return null;
  return look.style === 'standard' && !hasTextColors(look) ? null : look;
}
